import React from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { Collapse, Divider, Grid, Typography } from '@mui/material';

import { Box } from '@mui/system';
import { DateTime } from 'luxon';
import { SafetyEventReport, RadioGroupOption, Employee, EmployeePosition } from '../../../core/api/clients';
import { useSafetyEventReportData } from '../../../core/context/SafetyEventReportDataContext';
import ControlledRadioGroup from '../../../components/ControlledRadioGroup';
import ControlledTextField from '../../../components/ControlledTextField';
import ControlledPeoplePicker from '../../../components/ControlledPeoplePicker';
import ControlledDatePicker from '../../../components/ControlledDatePicker';
import ControlledTimeField from '../../../components/ControlledTimeField';

function CrewDebriefStep({ visible, readOnly }: { visible: boolean, readOnly?: boolean }) {
  const { control, setValue } = useFormContext<SafetyEventReport>();
  const { safetyEventReport, allowedActions } = useSafetyEventReportData();

  const tsbNotification = useWatch<SafetyEventReport>({ control, name: 'tsbNotification' });
  const ntsbNotification = useWatch<SafetyEventReport>({ control, name: 'ntsbNotification' });
  const pressRelease = useWatch<SafetyEventReport>({ control, name: 'pressRelease' });
  const internalAnnouncement = useWatch<SafetyEventReport>({ control, name: 'internalAnnouncement' });
  const eventReviewRequired = useWatch<SafetyEventReport>({ control, name: 'eventReviewRequired' });
  const eventReviewComments = useWatch<SafetyEventReport>({ control, name: 'eventReviewComments' });
  return (
    <Collapse in={visible}>
      <Box mb={1}>
        <Typography variant="h6">Decisions</Typography>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <ControlledDatePicker<SafetyEventReport>
            name="crewDebriefDate"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <ControlledTimeField
            name="crewDebriefTime"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            name="captainFitForDuty"
            control={control}
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
            onToggle={() => setValue('captainFitForDuty', null)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            name="firstOfficerFitForDuty"
            control={control}
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
            onToggle={() => setValue('firstOfficerFitForDuty', null)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            name="flightAttendantFitForDuty"
            control={control}
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
            onToggle={() => setValue('flightAttendantFitForDuty', null)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            name="eventReviewRequired"
            control={control}
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit && eventReviewComments ? eventReviewComments.toString().length > 0 : false}
            isEditable={eventReviewComments ? eventReviewComments.toString().length <= 0 : true}
            onToggle={() => setValue('eventReviewRequired', null)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            name="fdrDownloadRemoval"
            control={control}
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
            onToggle={() => setValue('fdrDownloadRemoval', null)}
            label="FDR Download Removal"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            name="cvrDownload"
            control={control}
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
            onToggle={() => setValue('cvrDownload', null)}
            label="CVR Download"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            name="crewReleasedToOperation"
            control={control}
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
            onToggle={() => setValue('crewReleasedToOperation', null)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            name="aircraftReleasedToOperation"
            control={control}
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
            onToggle={() => setValue('aircraftReleasedToOperation', null)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledRadioGroup<SafetyEventReport>
            RadioGroupProps={{ row: true }}
            name="drugTestCrew"
            control={control}
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
            onToggle={() => setValue('drugTestCrew', null)}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <ControlledTextField<SafetyEventReport>
            name="releaseComment"
            control={control}
            readOnly={readOnly}
            TextFieldProps={{
              multiline: true,
              rows: 3,
            }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <ControlledTextField<SafetyEventReport>
            name="additionalDecisions"
            control={control}
            readOnly={readOnly}
            TextFieldProps={{
              multiline: true,
              rows: 6,
            }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Box mb={1}>
        <Typography variant="h6">Actions</Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ControlledRadioGroup<SafetyEventReport>
                RadioGroupProps={{ row: true }}
                name="ntsbNotification"
                control={control}
                radioOptions={[
                  { label: 'Yes', value: RadioGroupOption.Yes },
                  { label: 'No', value: RadioGroupOption.No },
                ]}
                readOnly={readOnly}
                disableEditPopper={!allowedActions?.edit || (safetyEventReport.ntsbNotified ?? false)}
                label="NTSB Notification"
                onToggle={() => setValue('ntsbNotification', null)}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledRadioGroup<SafetyEventReport>
                RadioGroupProps={{ row: true }}
                name="afaNotification"
                control={control}
                radioOptions={[
                  { label: 'Yes', value: RadioGroupOption.Yes },
                  { label: 'No', value: RadioGroupOption.No },
                ]}
                readOnly={readOnly}
                disableEditPopper={!allowedActions?.edit || (safetyEventReport.afaNotified ?? false)}
                label="AFA Notification"
                onToggle={() => setValue('afaNotification', null)}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledRadioGroup<SafetyEventReport>
                RadioGroupProps={{ row: true }}
                name="alpaNotification"
                control={control}
                radioOptions={[
                  { label: 'Yes', value: RadioGroupOption.Yes },
                  { label: 'No', value: RadioGroupOption.No },
                ]}
                readOnly={readOnly}
                disableEditPopper={!allowedActions?.edit || (safetyEventReport.alpaNotified ?? false)}
                label="ALPA Notification"
                onToggle={() => setValue('alpaNotification', null)}
              />
            </Grid>
            <Grid item xs={12} />
          </Grid>
        </Grid>
        <Grid item xs={12} sm={10}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3} md={2}>
              <ControlledRadioGroup<SafetyEventReport>
                RadioGroupProps={{ row: true }}
                name="tsbNotification"
                control={control}
                radioOptions={[
                  { label: 'Yes', value: RadioGroupOption.Yes },
                  { label: 'No', value: RadioGroupOption.No },
                ]}
                readOnly={readOnly}
                disableEditPopper={!allowedActions?.edit}
                label="TSB Notification"
                onToggle={() => setValue('tsbNotification', null)}
              />
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
              <Collapse in={tsbNotification === RadioGroupOption.Yes} timeout={0}>
                <ControlledPeoplePicker<SafetyEventReport>
                  groupName="tsbNotificationPerson"
                  name="tsbNotificationName"
                  position={EmployeePosition.All}
                  control={control}
                  fieldNames={['tsbNotificationId', 'tsbNotificationName', 'tsbNotificationDomicile']}
                  onPersonSelected={(employee: Employee | null) => {
                    setValue('tsbNotificationName', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
                    setValue('tsbNotificationId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
                    setValue('tsbNotificationDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
                  }}
                  readOnly={readOnly}
                  rules={{ required: tsbNotification === RadioGroupOption.Yes }}
                  disableEditPopper={!allowedActions?.edit}
                  label="TSB Notification Person"
                />
              </Collapse>
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <ControlledRadioGroup<SafetyEventReport>
                RadioGroupProps={{ row: true }}
                name="pressRelease"
                control={control}
                radioOptions={[
                  { label: 'Yes', value: RadioGroupOption.Yes },
                  { label: 'No', value: RadioGroupOption.No },
                ]}
                readOnly={readOnly}
                disableEditPopper={!allowedActions?.edit}
                onToggle={() => setValue('pressRelease', null)}
              />
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
              <Collapse in={pressRelease === RadioGroupOption.Yes} timeout={0}>
                <ControlledPeoplePicker<SafetyEventReport>
                  groupName="pressReleasePerson"
                  name="pressReleaseName"
                  position={EmployeePosition.All}
                  control={control}
                  fieldNames={['pressReleaseId', 'pressReleaseName', 'pressReleaseDomicile']}
                  onPersonSelected={(employee: Employee | null) => {
                    setValue('pressReleaseName', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
                    setValue('pressReleaseId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
                    setValue('pressReleaseDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
                  }}
                  readOnly={readOnly}
                  rules={{ required: pressRelease === RadioGroupOption.Yes }}
                  disableEditPopper={!allowedActions?.edit}
                />
              </Collapse>
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <ControlledRadioGroup<SafetyEventReport>
                RadioGroupProps={{ row: true }}
                name="internalAnnouncement"
                control={control}
                radioOptions={[
                  { label: 'Yes', value: RadioGroupOption.Yes },
                  { label: 'No', value: RadioGroupOption.No },
                ]}
                readOnly={readOnly}
                disableEditPopper={!allowedActions?.edit}
                onToggle={() => setValue('internalAnnouncement', null)}
              />
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
              <Collapse in={internalAnnouncement === RadioGroupOption.Yes} timeout={0}>
                <ControlledPeoplePicker<SafetyEventReport>
                  groupName="internalAnnouncementPerson"
                  name="internalAnnouncementName"
                  position={EmployeePosition.All}
                  control={control}
                  fieldNames={['internalAnnouncementId', 'internalAnnouncementName', 'internalAnnouncementDomicile']}
                  onPersonSelected={(employee: Employee | null) => {
                    setValue('internalAnnouncementName', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
                    setValue('internalAnnouncementId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
                    setValue('internalAnnouncementDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
                  }}
                  readOnly={readOnly}
                  rules={{ required: internalAnnouncement === RadioGroupOption.Yes }}
                  disableEditPopper={!allowedActions?.edit}
                />
              </Collapse>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <ControlledTextField<SafetyEventReport>
            name="additionalComments"
            control={control}
            readOnly={readOnly}
            TextFieldProps={{
              multiline: true,
              rows: 6,
            }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        {(eventReviewRequired === RadioGroupOption.Yes || (eventReviewComments ? eventReviewComments.toString().length > 0 : false))
        && (
          <Grid item sm={12} md={12}>
            <Typography variant="h6">Event Review</Typography>
            <Grid item xs={12} sm={12} md={12} mt={1}>
              <ControlledTextField<SafetyEventReport>
                name="eventReviewComments"
                control={control}
                readOnly={readOnly}
                TextFieldProps={{
                  multiline: true,
                  rows: 6,
                }}
                disableEditPopper={!allowedActions?.edit}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </Collapse>
  );
}

export default CrewDebriefStep;
